/**
 * Параллакс от указателя: смещение слоя в пикселях по положению мыши
 * относительно центра окна. Положительная глубина — слой идёт за мышью,
 * отрицательная — навстречу; два слоя с разными знаками дают ощущение
 * объёма. Пружина сглаживает рывки; на сенсорных экранах и при «меньше
 * движения» слой стоит на месте.
 */

import { useMotionValue, useReducedMotion, useSpring, type MotionValue } from 'framer-motion';
import { useEffect } from 'react';

const SPRING = { stiffness: 60, damping: 18, mass: 0.6 } as const;

export function usePointerParallax(depth: number): { readonly x: MotionValue<number>; readonly y: MotionValue<number> } {
  const reduceMotion = useReducedMotion();
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const x = useSpring(rawX, SPRING);
  const y = useSpring(rawY, SPRING);

  useEffect(() => {
    if (reduceMotion === true || !window.matchMedia('(pointer: fine)').matches) return;
    const onMove = (event: PointerEvent) => {
      // −1…1 от центра окна.
      rawX.set(((event.clientX / window.innerWidth) * 2 - 1) * depth);
      rawY.set(((event.clientY / window.innerHeight) * 2 - 1) * depth);
    };
    const onLeave = () => {
      rawX.set(0);
      rawY.set(0);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('pointerleave', onLeave);
    };
  }, [depth, reduceMotion, rawX, rawY]);

  return { x, y };
}
